/**
 * UAPC Anytime - Admin Dashboard
 * @module AdminDashboard
 * @author Osman - Frontend Developer
 * @author Roohi Fatima - Frontend Developer
 * @description Admin main dashboard with system-wide stats
 * @version 1.0.0
 * Git: Osman <osman@linesoft.com> - Dashboard layout
 * Git: Roohi Fatima <roohi@linesoft.com> - Stats components
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Package, ShoppingCart, DollarSign, Clock, ChevronRight, AlertTriangle } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/admin`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-slate-200 dark:bg-slate-800 w-64" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-200 dark:bg-slate-800" />
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8" data-testid="admin-dashboard">
        <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Admin Dashboard
        </h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Total Vendors</span>
            </div>
            <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="total-vendors">
              {stats?.total_vendors || 0}
            </div>
            {stats?.pending_vendors > 0 && (
              <div className="mt-2 flex items-center gap-1 text-orange-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                {stats.pending_vendors} pending approval
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Total Parts</span>
            </div>
            <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="total-parts">
              {stats?.total_parts || 0}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-orange-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Total Orders</span>
            </div>
            <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="total-orders">
              {stats?.total_orders || 0}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Total Revenue</span>
            </div>
            <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="total-revenue">
              ${stats?.total_revenue?.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Vendors */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase">
                Recent Vendors
              </h2>
              <Link to="/admin/vendors" className="text-blue-600 hover:text-blue-700 text-sm font-bold flex items-center gap-1">
                Manage <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {stats?.recent_vendors?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_vendors.map((vendor, index) => (
                  <div key={vendor.id} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {vendor.company_name || vendor.name}
                      </div>
                      <div className="text-sm text-slate-500">{vendor.email}</div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-bold uppercase ${
                      vendor.is_approved ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {vendor.is_approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No recent vendors</p>
            )}
          </div>

          {/* Recent Orders */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase">
                Recent Orders
              </h2>
              <Link to="/admin/reports" className="text-blue-600 hover:text-blue-700 text-sm font-bold flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {stats?.recent_orders?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_orders.map((order, index) => (
                  <div key={order.id} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                    <div>
                      <div className="font-mono text-xs text-slate-500">#{order.id.slice(0, 8)}</div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {order.items?.length || 0} items
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-bold text-blue-600">
                        ${order.total?.toFixed(2)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No recent orders</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
